/**
 * Enterprise Navigation Overhaul — nested, accordion-style, registry-
 * driven left navigation (supersedes the old flat `nav-config.ts`
 * list). Top-level sections expand/collapse one at a time
 * (`layoutStore.expandedSectionId`); a section with zero
 * permission-visible descendants renders nothing at all. Favorites and
 * Recent Items are pinned above the registry tree. Collapsed mode
 * (icon-only, existing `COLLAPSED_WIDTH` behavior) shows a flyout menu
 * of a section's leaves on click, since there's no room for nested
 * indentation at that width.
 */
import { useState, type JSX, type MouseEvent } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FolderIcon from '@mui/icons-material/Folder';
import type { SvgIconComponent } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLayoutStore } from '@store/layout.store';
import { useAuthStore } from '@store/auth.store';
import { useEffectiveModuleCodes } from '@hooks/useEffectiveModuleCodes';
import {
  getBreadcrumbTrail,
  getChildren,
  getFlatNavForWorkspace,
  getSectionsForWorkspace,
  hasAccess,
  hueOf,
  isVisible,
  getTopLevelSections,
  trackNavigation,
  type NavRegistryItem,
} from '@config/navigation-registry';
import { useWorkspaceStore } from '@store/workspace.store';
import { useNavBadge } from './badge-providers';
import { HEADER_HEIGHT } from './Header';

const EXPANDED_WIDTH = 232;
const COLLAPSED_WIDTH = 64;

/*
  THE VISIBILITY RULE NOW LIVES IN THE REGISTRY, and these are re-exports.

  Both functions were defined here while the sidebar was their only reader.
  `getLandingPathForWorkspace` asks the same question — "may this user open this
  row?" — to decide where `/` lands, and two copies of that rule is how the
  sidebar and the screen you are dropped on start disagreeing about what you can
  see. The definitions moved to `navigation-registry.ts` unchanged.

  Re-exported rather than removed so `Sidebar.test.ts` and any other caller keep
  working, and so the component remains the integration point it always was.

  IMPORTED as well as re-exported: `export … from` forwards the names without
  binding them locally, and this file calls `isVisible` in four places.
*/
export { hasAccess, isVisible };

function flattenLeaves(item: NavRegistryItem, permissions: string[], moduleCodes: string[]): NavRegistryItem[] {
  const children = getChildren(item.id);
  if (children.length === 0) return item.path ? [item] : [];
  return children
    .filter((child) => isVisible(child, permissions, moduleCodes))
    .flatMap((child) => flattenLeaves(child, permissions, moduleCodes));
}

function NavBadge({ item }: { item: NavRegistryItem }): JSX.Element | null {
  const count = useNavBadge(item.badgeSource);
  const value = item.badge === 'new' ? 'New' : (item.badge ?? count);
  if (!value) return null;
  return <Badge badgeContent={value} color="primary" sx={{ mr: 1 }} />;
}

function FavoriteToggle({ item }: { item: NavRegistryItem }): JSX.Element | null {
  const favoriteIds = useLayoutStore((state) => state.favoriteIds);
  const toggleFavorite = useLayoutStore((state) => state.toggleFavorite);
  if (item.favoriteAllowed === false || !item.path) return null;
  const isFavorite = favoriteIds.includes(item.id);
  return (
    <IconButton
      size="small"
      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      sx={{ color: 'sidebar.iconColor' }}
      onClick={(event) => {
        event.stopPropagation();
        toggleFavorite(item.id);
      }}
    >
      {isFavorite ? <StarIcon fontSize="small" color="warning" /> : <StarBorderIcon fontSize="small" />}
    </IconButton>
  );
}

interface NavNodeProps {
  item: NavRegistryItem;
  depth: number;
  collapsed: boolean;
  /** Ids of the registry items on the current URL's breadcrumb trail (`getBreadcrumbTrail()`, root-first ancestry of whichever leaf's `path` best matches the route) — computed once in `Sidebar` and threaded down so every node's active state agrees with `Breadcrumbs.tsx`, including on dynamic detail routes (e.g. `/patients/:patientId`) where no registry entry matches exactly. */
  selectedIds: Set<string>;
  /** Called after every navigation the tree triggers — on mobile, `Sidebar` uses this to dismiss the temporary overlay drawer once a destination is picked (an overlay that outlives the click it caused would just be a new dead control). No-op on desktop. */
  onNavigate: () => void;
  /**
   * The nearest ancestor section's icon, threaded down so a leaf that names no
   * icon of its own shows its WORKSTATION's symbol instead of a folder — the
   * same inheritance `flattenSectionPages` already does for the flat rails.
   */
  inheritedIcon?: SvgIconComponent;
}

function NavNode({ item, depth, collapsed, selectedIds, onNavigate, inheritedIcon }: NavNodeProps): JSX.Element | null {
  const permissions = useAuthStore((state) => state.permissions);
  const moduleCodes = useEffectiveModuleCodes();
  const navigate = useNavigate();
  const expandedSectionId = useLayoutStore((state) => state.expandedSectionId);
  const setExpandedSectionId = useLayoutStore((state) => state.setExpandedSectionId);
  const [flyoutAnchor, setFlyoutAnchor] = useState<HTMLElement | null>(null);

  if (!isVisible(item, permissions, moduleCodes)) return null;

  const children = getChildren(item.id).filter((child) => isVisible(child, permissions, moduleCodes));
  /*
    A LEAF WITHOUT ITS OWN ICON INHERITS ITS SECTION'S — never a folder.

    This was `item.icon ?? FolderIcon`, so every row that did not name an icon
    rendered as a generic folder: Front Desk's six rows, the Laboratory stages,
    the Pharmacy screens, all of Finance. The workstation RAILS have always
    drawn real symbols for the same rows (`WORKSPACE_FLAT_NAV` supplies one per
    entry, and `flattenSectionPages` inherits down the tree), so the two views
    of the same sidebar disagreed: a rail of meaningful glyphs, a tree of
    identical folders.

    Inheriting is what the flat rail already does, so this makes the tree agree
    with it rather than inventing a third rule. A folder icon says "there is
    something inside me", which is exactly what a leaf is not.
  */
  const Icon = item.icon ?? inheritedIcon ?? (depth > 0 ? FolderIcon : undefined);
  const isGroup = children.length > 0;
  // Same matching rule Breadcrumbs.tsx uses (getBreadcrumbTrail -> pathsMatch),
  // not a bare `item.path === location.pathname + location.search` — that
  // exact-only check both (a) let two registry entries sharing one physical
  // path show "active" simultaneously, since it can't discriminate deeper
  // than the URL when the URL itself is required to differ (now fixed at
  // the registry level for the clinical OPD items), and (b) never
  // highlighted a group's leaf on a dynamic child route the leaf's static
  // `path` is only a prefix of (e.g. `/patients` vs. `/patients/:patientId`).
  const selected = !isGroup && selectedIds.has(item.id);

  if (collapsed && depth === 0) {
    // Icon-only top level: a click opens a flyout listing every visible leaf underneath (flattened — no room for nested indentation at this width).
    const leaves = isGroup ? flattenLeaves(item, permissions, moduleCodes) : item.path ? [item] : [];
    return (
      <>
        <Tooltip title={item.label} placement="right">
          <ListItemButton
            sx={{ justifyContent: 'center', px: 1.5, py: 0.75 }}
            onClick={(event: MouseEvent<HTMLElement>) => {
              if (isGroup) {
                setFlyoutAnchor(event.currentTarget);
                return;
              }
              if (item.path) {
                navigate(item.path);
                onNavigate();
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, justifyContent: 'center', color: 'sidebar.iconColor' }}>
              {Icon && <Icon fontSize="small" />}
            </ListItemIcon>
          </ListItemButton>
        </Tooltip>
        {isGroup && (
          <Menu anchorEl={flyoutAnchor} open={Boolean(flyoutAnchor)} onClose={() => setFlyoutAnchor(null)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
            {leaves.map((leaf) => (
              <MenuItem
                key={leaf.id}
                onClick={() => {
                  setFlyoutAnchor(null);
                  trackNavigation({ itemId: leaf.id, action: 'click' });
                  navigate(leaf.path!);
                  onNavigate();
                }}
              >
                {leaf.label}
              </MenuItem>
            ))}
          </Menu>
        )}
      </>
    );
  }

  if (isGroup) {
    const expanded = depth === 0 ? expandedSectionId === item.id : true; // only top-level sections are accordion-collapsible; nested groups always show
    return (
      <>
        <ListItemButton onClick={() => (depth === 0 ? setExpandedSectionId(item.id) : undefined)} sx={{ pl: 2 + depth * 2, py: 0.75 }}>
          <ListItemIcon sx={{ minWidth: 40, color: 'sidebar.iconColor' }}>{Icon && <Icon fontSize="small" />}</ListItemIcon>
          {/* A section header is bolder than the leaves it opens, so the rail
              still has a hierarchy now that every row is bold. */}
          <ListItemText primary={item.label} slotProps={{ primary: { sx: { color: 'sidebar.textPrimary', fontWeight: 700 } } }} />
          {depth === 0 &&
            (expanded ? (
              <ExpandLessIcon fontSize="small" sx={{ color: 'sidebar.iconColor' }} />
            ) : (
              <ExpandMoreIcon fontSize="small" sx={{ color: 'sidebar.iconColor' }} />
            ))}
        </ListItemButton>
        <Collapse in={expanded} unmountOnExit>
          <List component="div" disablePadding>
            {children.map((child) => (
              <NavNode
                key={child.id}
                item={child}
                depth={depth + 1}
                collapsed={collapsed}
                selectedIds={selectedIds}
                onNavigate={onNavigate}
                inheritedIcon={Icon}
              />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  // Resolved per row rather than threaded down: a leaf reached from the flat
  // rail has no parent node rendering above it to inherit from.
  const hue = hueOf(item.id);

  return (
    <ListItemButton
      selected={selected}
      onClick={() => {
        if (!item.path) return;
        trackNavigation({ itemId: item.id, action: 'click' });
        navigate(item.path);
        onNavigate();
      }}
      sx={{
        pl: 2 + depth * 2 - (selected ? 0.375 : 0),
        py: 0.75,
        // A ROUNDED PILL, Google's own nav treatment. A full-bleed strip of
        // near-identical colour was the part that read as a smudge; a pill with
        // a clear edge reads as "you are here" from across a counter.
        mx: 0.75,
        borderRadius: 2,
        borderLeft: selected ? '3px solid' : '3px solid transparent',
        // THE SECTION'S OWN HUE, not one global accent. Pharmacy is green,
        // Front Office is blue — and the page that opens carries the same tint,
        // so the rail and the screen agree about where the operator is.
        borderLeftColor: selected ? `var(--nx-${hue})` : 'transparent',
        '&:hover': { backgroundColor: 'sidebar.backgroundHover' },
        '&.Mui-selected': { backgroundColor: `var(--nx-${hue}-tint)` },
        '&.Mui-selected:hover': { backgroundColor: `var(--nx-${hue}-tint)` },
        '&:hover .nav-favorite-toggle': { visibility: 'visible' },
      }}
    >
      <ListItemIcon sx={{ minWidth: 40, color: selected ? `var(--nx-${hue}-on-tint)` : `var(--nx-${hue})` }}>
        {Icon && <Icon fontSize="small" />}
      </ListItemIcon>
      <ListItemText
        primary={item.label}
        slotProps={{
          // The LABEL changes with the state too. Recolouring only the
          // background left the selected row's text the same weight and colour
          // as every other row, so the state depended entirely on one fill —
          // which is also the WCAG 1.4.1 problem: colour as the only carrier.
          primary: {
            sx: {
              color: selected ? `var(--nx-${hue}-on-tint)` : 'sidebar.textPrimary',
              // Asked for by the desk: the rail reads as bold throughout. The
              // SELECTED row still steps up a weight above the rest — the
              // WCAG 1.4.1 point above only holds while the two differ, so
              // both were raised rather than flattened to one weight.
              fontWeight: selected ? 700 : 600,
            },
          },
        }}
      />
      <NavBadge item={item} />
      <span className="nav-favorite-toggle" style={{ visibility: 'hidden' }}>
        <FavoriteToggle item={item} />
      </span>
    </ListItemButton>
  );
}

function FavoritesSection({ onNavigate }: { onNavigate: () => void }): JSX.Element | null {
  const favoriteIds = useLayoutStore((state) => state.favoriteIds);
  const reorderFavorite = useLayoutStore((state) => state.reorderFavorite);
  const permissions = useAuthStore((state) => state.permissions);
  const moduleCodes = useEffectiveModuleCodes();
  const navigate = useNavigate();
  const allLeaves = getTopLevelSections().flatMap((section) => flattenLeaves(section, permissions, moduleCodes));
  const favorites = favoriteIds.map((id) => allLeaves.find((leaf) => leaf.id === id)).filter((item): item is NavRegistryItem => Boolean(item));

  if (favorites.length === 0) return null;

  return (
    <>
      {/* A white-alpha rule was invisible the moment the sidebar stopped being
          dark; the border token adapts with the theme. */}
      <Divider sx={{ borderColor: 'sidebar.border' }} />
      <ListSubheader sx={{ bgcolor: 'sidebar.background', color: 'sidebar.textSecondary' }}>Favorites</ListSubheader>
      {favorites.map((item, index) => (
        <ListItemButton
          key={item.id}
          onClick={() => {
            if (!item.path) return;
            navigate(item.path);
            onNavigate();
          }}
          dense
          sx={{ '&:hover': { backgroundColor: 'sidebar.backgroundHover' } }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <StarIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText primary={item.label} slotProps={{ primary: { sx: { color: 'sidebar.textPrimary', fontWeight: 600 } } }} />
          <IconButton size="small" sx={{ color: 'sidebar.iconColor' }} disabled={index === 0} onClick={(event) => { event.stopPropagation(); reorderFavorite(item.id, 'up'); }}>
            <ArrowUpwardIcon fontSize="inherit" />
          </IconButton>
          <IconButton size="small" sx={{ color: 'sidebar.iconColor' }} disabled={index === favorites.length - 1} onClick={(event) => { event.stopPropagation(); reorderFavorite(item.id, 'down'); }}>
            <ArrowDownwardIcon fontSize="inherit" />
          </IconButton>
        </ListItemButton>
      ))}
    </>
  );
}

/**
 * "Recent Pages" (page-visit history) and "Recent Patients" (recently
 * viewed patients) were both removed from the sidebar per explicit
 * request — the navigation rail now shows navigation and favourites only.
 * The recently-viewed store still records views; nothing renders them.
 */

export function Sidebar(): JSX.Element {
  const storedCollapsed = useLayoutStore((state) => state.sidebarCollapsed);
  const mobileOpen = useLayoutStore((state) => state.mobileSidebarOpen);
  const setMobileSidebarOpen = useLayoutStore((state) => state.setMobileSidebarOpen);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // The icon-rail collapse is a desktop-only space-saving preference; below
  // `sm` the sidebar is a full-width temporary overlay instead (open/closed
  // driven by `mobileOpen`, not by this), so it's never forced collapsed
  // here the way it used to be (`storedCollapsed || isMobile`) — that old
  // formula was what pinned mobile to a permanent 64px rail with no way to
  // expand it.
  const collapsed = !isMobile && storedCollapsed;
  const permissions = useAuthStore((state) => state.permissions);
  const moduleCodes = useEffectiveModuleCodes();
  const location = useLocation();
  const currentWorkspaceId = useWorkspaceStore((state) => state.currentWorkspaceId);

  const width = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;
  // A workspace may declare a flat, short sidebar instead of the full tree —
  // see `WORKSPACE_FLAT_NAV`. Permission and module gating below is unchanged
  // either way; this only decides WHICH items are offered, never whether the
  // user may see them.
  const flatNav = getFlatNavForWorkspace(currentWorkspaceId);
  const sections = flatNav ?? getSectionsForWorkspace(currentWorkspaceId);
  // Computed once per render and threaded down to every NavNode, rather than
  // each node independently re-deriving it — keeps the active-state rule
  // identical to (and as cheap as) Breadcrumbs.tsx's single call.
  const selectedIds = new Set(getBreadcrumbTrail(location.pathname + location.search).map((item) => item.id));
  // Dismiss the mobile overlay once a destination is actually picked; a
  // no-op on desktop, where the drawer is permanent and never "open" in
  // this sense.
  const handleNavigate = (): void => {
    if (isMobile) setMobileSidebarOpen(false);
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={() => setMobileSidebarOpen(false)}
      ModalProps={isMobile ? { keepMounted: true } : undefined}
      sx={{
        width,
        flexShrink: 0,
        transition: (theme) => theme.transitions.create('width'),
        [`& .MuiDrawer-paper`]: {
          width,
          boxSizing: 'border-box',
          top: isMobile ? 0 : HEADER_HEIGHT,
          height: isMobile ? '100%' : `calc(100% - ${HEADER_HEIGHT}px)`,
          overflowX: 'hidden',
          overflowY: 'auto',
          transition: (theme) => theme.transitions.create('width'),
          bgcolor: 'sidebar.background',
          color: 'sidebar.textPrimary',
          // `borderRight: 'none'` was right for a dark bar, which separated
          // itself from the page by tone alone. A light sidebar against a light
          // page needs a real edge.
          borderRight: '1px solid',
          borderRightColor: 'sidebar.border',
        },
      }}
    >
      <List component="nav" aria-label="Main navigation" dense={collapsed}>
        {sections
          .filter((section) => isVisible(section, permissions, moduleCodes))
          .map((section) => (
            <NavNode key={section.id} item={section} depth={0} collapsed={collapsed} selectedIds={selectedIds} onNavigate={handleNavigate} />
          ))}
        {!collapsed && <FavoritesSection onNavigate={handleNavigate} />}
      </List>
    </Drawer>
  );
}

export { EXPANDED_WIDTH, COLLAPSED_WIDTH };
